/**
 * Modale "Créer une tâche de suivi".
 */
import React, { useState } from 'react';
import axios from 'axios';
import { Dialog, DialogContent } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Textarea } from '../../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../ui/select';
import { X, ClipboardList, Calendar, Clock as ClockIcon } from 'lucide-react';
import { useClientActions } from './ClientActionsContext';
import { useToast } from '../../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { id: 'recall', label: 'Relance client' },
  { id: 'call', label: 'Appel' },
  { id: 'email', label: 'Email' },
  { id: 'meeting', label: 'Rendez-vous' },
  { id: 'commercial', label: 'Suivi commercial' },
];
const PRIORITIES = [
  { id: 'haute', label: 'Haute', dot: 'bg-red-500' },
  { id: 'moyenne', label: 'Moyenne', dot: 'bg-amber-500' },
  { id: 'basse', label: 'Faible', dot: 'bg-emerald-500' },
];
const ASSIGNEES = [
  { id: 'me', label: 'Admin Baobab' },
  { id: 'lead_com', label: 'Lead Commercial' },
  { id: 'finance', label: 'Finance' },
];

function tomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().slice(0, 10);
}

export default function TaskModal() {
  const { modal, client, close } = useClientActions();
  const { toast } = useToast();
  const isOpen = modal === 'task' && !!client;

  const [title, setTitle] = useState('');
  const [type, setType] = useState('recall');
  const [assignee, setAssignee] = useState('me');
  const [dueDate, setDueDate] = useState(tomorrowISO());
  const [dueTime, setDueTime] = useState('09:00');
  const [priority, setPriority] = useState('moyenne');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  React.useEffect(() => {
    if (client) setTitle(`Relance — ${client.companyName}`);
  }, [client]);

  if (!isOpen) return null;

  const handleCreate = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/admin/customer-success/actions`, {
        companyName: client.companyName,
        clientEmail: client.primaryEmail,
        action: 'task',
        subject: title,
        body: notes || null,
        visibility: 'commercial',
        metadata: { type, assignee, dueDate, dueTime, priority },
      });
      toast({ title: 'Tâche créée', description: `${title} — échéance ${dueDate} ${dueTime}.` });
      close();
    } catch (e) {
      toast({ title: 'Erreur', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md p-0 gap-0 overflow-hidden flex flex-col" data-testid="task-modal">
        <div className="px-6 pt-6 pb-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
              <ClipboardList className="w-4 h-4 text-emerald-700" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Créer une tâche</h2>
              <p className="text-xs text-gray-500">Planifiez un suivi pour {client.companyName}</p>
            </div>
          </div>
          <button onClick={close} className="text-gray-400 hover:text-gray-600" data-testid="close-task-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-6 pb-4 space-y-3 overflow-auto">
          <div>
            <label className="text-xs text-gray-500 block mb-1">Titre de la tâche</label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 block mb-1">Type de tâche</label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="task-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TYPES.map((t) => <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs text-gray-500 block mb-1">Assignée à</label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger data-testid="task-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ASSIGNEES.map((a) => <SelectItem key={a.id} value={a.id}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" />Échéance</label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="task-due-date" />
            </div>
            <div>
              <label className="text-xs text-gray-500 mb-1 flex items-center gap-1"><ClockIcon className="w-3 h-3" />Heure</label>
              <Input type="time" value={dueTime} onChange={(e) => setDueTime(e.target.value)} data-testid="task-due-time" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Priorité</label>
            <Select value={priority} onValueChange={setPriority}>
              <SelectTrigger data-testid="task-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    <span className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${p.dot}`} /> {p.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-gray-500 block mb-1">Notes (optionnel)</label>
            <Textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Ajout de notes ou contexte spécifique..."
              data-testid="task-notes"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={close} data-testid="task-cancel-btn">Annuler</Button>
          <Button
            size="sm" onClick={handleCreate}
            disabled={!title.trim() || saving}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="task-create-btn"
          >
            {saving ? 'Création...' : 'Créer la tâche'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
